import { Fragment } from "react";
import { CornerDownRight, Plus, Trash2 } from "lucide-react";
import t from "@/lang/lang";
import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { Button } from "@/shared/components/ui/button";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableCellSelect,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PositionKind } from "../types/position-kind";
import { PositionUnitSelectItems } from "../types/position-unit";
import { PositionCategorySelectItems } from "../types/position-category";
import {
  newPositionBaseData,
  newPositionBaseDataSubPosition,
  PositionBaseData,
  PositionBaseDataSubPosition,
} from "../types/position-base-data";
import {
  OptionSubPositionDetailsDialog,
  PositionOptionAvailabilityItem,
  PositionOptionDetailsDialog,
  positionKindSelectItems,
} from "./position-option-details-dialog";
import { PricesUsedOf } from "../types/price-used";

// The text column grows with its texts up to a maximum; a long text
// scrolls within its input. A text sub-position spans the columns from
// the text on and fills them instead of widening them.
const textClassName = "max-w-xl";

/**
 * PositionOptionsTable edits the options offered to add positions, see
 * `positions.Option`. The table shows their main fields, a details dialog per
 * option and sub-position all of them. fieldName is the key of the options in
 * the base data, e.g. "options". availabilities are the places the options may
 * be offered at; with only one, every option is offered there and the dialog
 * hides the choice. New options are offered at the first. subPositionKinds are
 * the kinds the sub-positions of an option may have; new sub-positions are of
 * the first. serviceKind and serviceVariation are those of the base data, like
 * companyId, which owns the data sheets the options may reference: the service
 * variation of an option is one of the service kind, and new options are of
 * serviceVariation. pricesUsed are the prices a position may show, a rule of
 * the service kind.
 */
export function PositionOptionsTable({
  options,
  onOptionsChange,
  fieldName,
  companyId,
  serviceKind,
  serviceVariation,
  pricesUsed,
  availabilities,
  subPositionKinds,
  disabled,
}: {
  options: PositionBaseData[];
  onOptionsChange: (options: PositionBaseData[]) => void;
  fieldName: string;
  companyId: string;
  serviceKind: string;
  serviceVariation: ServiceVariation;
  pricesUsed: PricesUsedOf;
  availabilities: PositionOptionAvailabilityItem[];
  subPositionKinds: PositionKind[];
  disabled?: boolean;
}) {
  const columnCount = 6;
  const change = (i: number, option: PositionBaseData) =>
    onOptionsChange(options.map((o, j) => (j === i ? option : o)));
  const remove = (i: number) =>
    onOptionsChange(options.filter((_, j) => j !== i));
  const add = () =>
    onOptionsChange([
      ...options,
      {
        ...newPositionBaseData(PositionKind.Default, t("Neue Position")),
        serviceVariation,
        availableIn: availabilities.slice(0, 1).map((a) => a.value),
      },
    ]);
  const key = (i: number, field: string) => `${fieldName}.${i}.${field}`;
  const subPositionsOf = (option: PositionBaseData) =>
    option.subPositions ?? [];
  const changeSubPosition = (
    i: number,
    j: number,
    sub: PositionBaseDataSubPosition,
  ) =>
    change(i, {
      ...options[i],
      subPositions: subPositionsOf(options[i]).map((s, k) =>
        k === j ? sub : s,
      ),
    });
  const removeSubPosition = (i: number, j: number) =>
    change(i, {
      ...options[i],
      subPositions: subPositionsOf(options[i]).filter((_, k) => k !== j),
    });
  const addSubPosition = (i: number) =>
    change(i, {
      ...options[i],
      subPositions: [
        ...subPositionsOf(options[i]),
        newPositionBaseDataSubPosition(
          subPositionKinds[0] ?? PositionKind.Text,
        ),
      ],
    });

  return (
    <div className="grid gap-2">
      <ETable>
        <ETableHeader>
          <ETableRow>
            <ETableHead>{t("Art")}</ETableHead>
            <ETableHead className="w-full">{t("Text")}</ETableHead>
            <ETableHead>{t("Kategorie")}</ETableHead>
            <ETableHead>{t("Einheit")}</ETableHead>
            <ETableHead>{t("Preis / Einheit")}</ETableHead>
            <ETableHead />
          </ETableRow>
        </ETableHeader>
        <ETableBody>
          {options.length === 0 && <TableEmptyRow colSpan={columnCount} />}
          {options.map((option, i) => (
            // options have no identity of their own, their index is their
            // key in the backend as well
            <Fragment key={i}>
              <ETableRow>
                <ETableCellSelect
                  label={t("Art")}
                  items={positionKindSelectItems}
                  value={option.kind}
                  onValueChange={(kind) => change(i, { ...option, kind })}
                  fieldName={key(i, "kind")}
                  disabled={disabled}
                />
                <ETableCellInput
                  value={option.text}
                  onChange={(e) =>
                    change(i, { ...option, text: e.target.value })
                  }
                  fieldName={key(i, "text")}
                  className={textClassName}
                  disabled={disabled}
                />
                <ETableCellSelect
                  label={t("Kategorie")}
                  items={PositionCategorySelectItems}
                  value={option.category}
                  onValueChange={(category) =>
                    change(i, { ...option, category })
                  }
                  fieldName={key(i, "category")}
                  disabled={disabled}
                />
                <ETableCellSelect
                  label={t("Einheit")}
                  items={PositionUnitSelectItems}
                  value={option.unit}
                  onValueChange={(unit) => change(i, { ...option, unit })}
                  fieldName={key(i, "unit")}
                  disabled={disabled}
                />
                <ETableCellDecimalInput
                  value={option.unitPrice}
                  onValueChange={(unitPrice) =>
                    change(i, { ...option, unitPrice })
                  }
                  precision={2}
                  fieldName={key(i, "unitPrice")}
                  disabled={disabled}
                />
                <ETableCell className="flex p-0">
                  <PositionOptionDetailsDialog
                    option={option}
                    companyId={companyId}
                    serviceKind={serviceKind}
                    pricesUsed={pricesUsed}
                    availabilities={availabilities}
                    onChange={(changed) => change(i, changed)}
                    fieldName={`${fieldName}.${i}`}
                    disabled={disabled}
                  />
                  <Button
                    variant="ghost"
                    className="w-8 rounded-none"
                    aria-label={t("Unterposition hinzufügen")}
                    title={t("Unterposition hinzufügen")}
                    onClick={() => addSubPosition(i)}
                    disabled={disabled}
                  >
                    <CornerDownRight />
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-8 rounded-none"
                    aria-label={t("Entfernen")}
                    title={t("Entfernen")}
                    onClick={() => remove(i)}
                    disabled={disabled}
                  >
                    <Trash2 />
                  </Button>
                </ETableCell>
              </ETableRow>
              {subPositionsOf(option).map((sub, j) => (
                <SubPositionRow
                  key={j}
                  subPosition={sub}
                  onChange={(changed) => changeSubPosition(i, j, changed)}
                  onRemove={() => removeSubPosition(i, j)}
                  fieldName={key(i, `subPositions.${j}`)}
                  kinds={subPositionKinds}
                  serviceVariation={option.serviceVariation}
                  pricesUsed={pricesUsed}
                  columnCount={columnCount}
                  disabled={disabled}
                />
              ))}
            </Fragment>
          ))}
        </ETableBody>
      </ETable>
      {!disabled && (
        <Button variant="outline" size="sm" className="w-fit" onClick={add}>
          <Plus />
          {t("Position hinzufügen")}
        </Button>
      )}
    </div>
  );
}

/**
 * SubPositionRow edits a sub-position of an option below it. A text
 * spans the price columns, any other kind fills them like an option and
 * shows all its fields in a details dialog.
 */
function SubPositionRow({
  subPosition: sub,
  onChange,
  onRemove,
  fieldName,
  kinds,
  serviceVariation,
  pricesUsed,
  columnCount,
  disabled,
}: {
  subPosition: PositionBaseDataSubPosition;
  onChange: (sub: PositionBaseDataSubPosition) => void;
  onRemove: () => void;
  fieldName: string;
  kinds: PositionKind[];
  serviceVariation: ServiceVariation;
  pricesUsed: PricesUsedOf;
  columnCount: number;
  disabled?: boolean;
}) {
  const key = (field: string) => `${fieldName}.${field}`;
  const kindItems = positionKindSelectItems.filter((item) =>
    kinds.includes(item.value),
  );
  // the kind column
  const leadingColumns = 1;

  return (
    <ETableRow>
      {kindItems.length > 1 ? (
        <ETableCellSelect
          label={t("Art")}
          items={kindItems}
          value={sub.kind}
          onValueChange={(kind) => onChange({ ...sub, kind })}
          fieldName={key("kind")}
          disabled={disabled}
        />
      ) : (
        <ETableCell>
          <CornerDownRight className="ml-2 size-4" aria-hidden="true" />
        </ETableCell>
      )}
      {sub.kind === PositionKind.Text ? (
        <ETableCellInput
          value={sub.text}
          onChange={(e) => onChange({ ...sub, text: e.target.value })}
          fieldName={key("text")}
          placeholder={t("Text der Unterposition")}
          className="field-sizing-fixed w-full"
          disabled={disabled}
          colSpan={columnCount - leadingColumns - 1}
        />
      ) : (
        <>
          <ETableCellInput
            value={sub.text}
            onChange={(e) => onChange({ ...sub, text: e.target.value })}
            fieldName={key("text")}
            placeholder={t("Text der Unterposition")}
            className={textClassName}
            disabled={disabled}
          />
          <ETableCellSelect
            label={t("Kategorie")}
            items={PositionCategorySelectItems}
            value={sub.category}
            onValueChange={(category) => onChange({ ...sub, category })}
            fieldName={key("category")}
            disabled={disabled}
          />
          <ETableCellSelect
            label={t("Einheit")}
            items={PositionUnitSelectItems}
            value={sub.unit}
            onValueChange={(unit) => onChange({ ...sub, unit })}
            fieldName={key("unit")}
            disabled={disabled}
          />
          <ETableCellDecimalInput
            value={sub.unitPrice}
            onValueChange={(unitPrice) => onChange({ ...sub, unitPrice })}
            precision={2}
            fieldName={key("unitPrice")}
            disabled={disabled}
          />
        </>
      )}
      <ETableCell className="flex p-0">
        {sub.kind !== PositionKind.Text && (
          <OptionSubPositionDetailsDialog
            subPosition={sub}
            kinds={kinds}
            serviceVariation={serviceVariation}
            pricesUsed={pricesUsed}
            onChange={onChange}
            fieldName={fieldName}
            disabled={disabled}
          />
        )}
        <Button
          variant="ghost"
          className="w-8 rounded-none"
          aria-label={t("Unterposition entfernen")}
          title={t("Unterposition entfernen")}
          onClick={onRemove}
          disabled={disabled}
        >
          <Trash2 />
        </Button>
      </ETableCell>
    </ETableRow>
  );
}
